import { batchActions } from 'redux-batched-actions';
import { call, put, select } from 'redux-saga/effects';
import { updateArtboard } from 'containers/Artboards/actions';
import { activeArtboard } from 'containers/Artboards/selectors';
import { saveAppState } from 'containers/UndoRedoControl/actions';
import { LayoutMap } from 'models';
import { handleSagaError } from 'services/handleError';
import { deleteLayouts, setLayout } from '../actions';
import * as Models from '../models';
import { orderedActiveLayoutsBySectionId } from '../selectors';
import { CAROUSEL_ITEM_TYPES } from './addCarousel';

export function* deleteCarouselSlide(action: Models.Action.IDeleteCarouselSlide): Generator {
  try {
    const { carouselId, sectionId, slideId, slideNumber } = action.payload;
    const artboard: ReturnType<typeof activeArtboard> = yield select(activeArtboard);
    const layoutsBySectionId: ReturnType<typeof orderedActiveLayoutsBySectionId> = yield select(orderedActiveLayoutsBySectionId);
    const sectionLayouts = layoutsBySectionId.get(sectionId);
    const slideLayoutsToDelete = sectionLayouts?.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('carouselId') === carouselId &&
        carouselItem?.get('slideId') === slideId;
    });

    if (!slideLayoutsToDelete) {
      return;
    }

    const allCarouselSlides = sectionLayouts?.filter((layout) => {
      const carouselItem = (layout as LayoutMap).get('carouselItem');

      return carouselItem?.get('carouselId') === carouselId &&
        carouselItem?.get('type') === CAROUSEL_ITEM_TYPES.SLIDE;
    });

    const slidesToUpdate = allCarouselSlides?.filter((layout) => {
      const currentSlideNumber = (layout as LayoutMap).get('carouselItem')?.get('slideNumber') ?? 0;

      return currentSlideNumber > slideNumber;
    });

    const decrementedSlidesToUpdate = slidesToUpdate?.map((slideLayout) => {
      const currentCarouselItem = (slideLayout as LayoutMap).get('carouselItem');
      const currentSlideNumber = currentCarouselItem?.get('slideNumber') ?? 0;
      const updatedCarouselItem = currentCarouselItem?.set('slideNumber', (Math.max(currentSlideNumber - 1, 0)));

      return (slideLayout as LayoutMap).set('carouselItem', updatedCarouselItem);
    });

    const layoutIdsToDelete = slideLayoutsToDelete.map(layout => (layout as LayoutMap).get('id')).toArray();

    const currentLayoutIds = artboard.get('layoutIds');
    const updatedLayoutIds = currentLayoutIds.filterNot(layoutId => layoutIdsToDelete.includes(layoutId));
    const updatedArtboard = artboard.set('layoutIds', updatedLayoutIds);

    yield put(batchActions([
      saveAppState(),
      deleteLayouts(layoutIdsToDelete),
      ...(decrementedSlidesToUpdate ? decrementedSlidesToUpdate.map(decrementedSlide => setLayout(decrementedSlide)) : []),
      updateArtboard(updatedArtboard),
    ]));
  } catch (error) {
    yield call(handleSagaError, error, 'Layouts.deleteCarouselSlide', 'DeleteCarouselSlide');
  }
}
